/**
 * THE INTERACTIVE TRADE SIMULATOR — modeless, draggable, and it updates rather than multiplies.
 *
 * <h2>Two halves answering two different questions</h2>
 *
 * <pre>
 *   TOP     the clicked signal: what was knowable at bar t, and what the trade then did
 *   BOTTOM  every signal clicked this session, as one rolling ledger
 * </pre>
 *
 * <h2>The line the top half must not blur</h2>
 *
 * The odds are an ESTIMATE made from bars 0..t-1; the result is an OUTCOME measured over
 * t+1..t+10. Side by side those invite the reading that the estimate had seen the outcome, so
 * each half of the card is labelled with the window it was measured over and the two sit in
 * separate blocks. The engine keeps them in separate modules for the same reason.
 *
 * <h2>It computes nothing</h2>
 *
 * Every figure comes from `simulateTrade` or `sessionSummary`, and the summary comes from
 * `calculatePortfolioMetrics`. A panel that derived a win rate of its own would be a second
 * answer a centimetre from the first.
 */

import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import Draggable from 'react-draggable';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { glassSurface, glassText } from '../utils/glassSurface';
import { OUTCOMES } from '../utils/tradeSimEngine';
import { assessesAutomatically, providerFor } from '../utils/tradeSimProviders';
import { PHASES, promptFor } from '../utils/tradeSimManual';
import { selectedTrade, sessionSummary } from '../utils/tradeSimSession';

const mono = { fontFamily: '"Roboto Mono", ui-monospace, Menlo, Consolas, monospace' };

/**
 * The outcome palette.
 *
 * <p>OPEN is AMBER rather than grey, and that is the one worth stating: grey reads as "nothing
 * happened" and an unresolved trade is the opposite — something is happening and the answer is
 * not in yet. NEUTRAL is the grey one, because that genuinely is an expiry.
 */
export const OUTCOME_COLORS = Object.freeze({
  [OUTCOMES.WIN]: '#10B981',
  [OUTCOMES.LOSS]: '#EF4444',
  [OUTCOMES.NEUTRAL]: '#94a3b8',
  [OUTCOMES.OPEN]: '#F59E0B',
});

export const OUTCOME_LABELS = Object.freeze({
  [OUTCOMES.WIN]: 'WIN',
  [OUTCOMES.LOSS]: 'LOSS',
  [OUTCOMES.NEUTRAL]: 'NEUTRAL',
  [OUTCOMES.OPEN]: 'OPEN',
});

const price = (v, dp = 2) =>
  (Number.isFinite(v) ? v.toFixed(dp) : '—');
const signed = (v, dp = 2) =>
  (Number.isFinite(v) ? `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v).toFixed(dp)}` : '—');
const pct = (v, dp = 2) =>
  (Number.isFinite(v) ? `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v).toFixed(dp)}%` : '—');
/** NULL renders as `n/a`, never as a number. An absent profit factor is not a perfect score. */
const ratio = (v, dp = 2) => (Number.isFinite(v) ? v.toFixed(dp) : 'n/a');

/**
 * THE TRADE SHAPE, declared once.
 *
 * <p>Spelled out rather than `PropTypes.shape({})`: the loose form satisfies the type checker
 * and tells a reader nothing, and the lint rule correctly refuses it. This is also the only
 * written statement of what a session entry carries, so it doubles as the contract between the
 * engine, the session reducer and this panel.
 */
export const TRADE_SHAPE = PropTypes.shape({
  id: PropTypes.string,
  provider: PropTypes.string,
  date: PropTypes.string,
  signal: PropTypes.object,
  index: PropTypes.number,
  direction: PropTypes.oneOf(['up', 'down']),
  priced: PropTypes.bool,
  horizon: PropTypes.number,
  outcome: PropTypes.oneOf(Object.values(OUTCOMES)),
  exitReason: PropTypes.string,
  entryPrice: PropTypes.number,
  target: PropTypes.number,
  stop: PropTypes.number,
  targetAtr: PropTypes.number,
  stopAtr: PropTypes.number,
  exitPrice: PropTypes.number,
  barsHeld: PropTypes.number,
  pnl: PropTypes.number,
  pnlPercent: PropTypes.number,
  rMultiple: PropTypes.number,
  // The OPEN case carries a MARK on its own fields, so nothing can fold it into a realised
  // tally by reading `pnlPercent` off an unresolved trade.
  markPrice: PropTypes.number,
  markPnlPercent: PropTypes.number,
  maeR: PropTypes.number,
  maePercent: PropTypes.number,
  mfeR: PropTypes.number,
  mfePercent: PropTypes.number,
  holdReturnPercent: PropTypes.number,
  alphaPercent: PropTypes.number,
});

/** What `sessionSummary` returns. */
export const SUMMARY_SHAPE = PropTypes.shape({
  clicks: PropTypes.number.isRequired,
  evaluated: PropTypes.number.isRequired,
  realised: PropTypes.number.isRequired,
  open: PropTypes.number.isRequired,
  unpriced: PropTypes.number.isRequired,
  wins: PropTypes.number.isRequired,
  losses: PropTypes.number.isRequired,
  neutral: PropTypes.number.isRequired,
  winRatePercent: PropTypes.number,
  profitFactor: PropTypes.number,
  totalNetPnlPercent: PropTypes.number,
  averageRMultiple: PropTypes.number,
  rMeasured: PropTypes.number,
  maxDrawdownPercent: PropTypes.number,
  totalPoints: PropTypes.number,
  // the institutional half — every one nullable, because every one has inputs that can be
  // absent and a zero would be a reading rather than an absence
  alphaPercent: PropTypes.number,
  holdReturnPercent: PropTypes.number,
  alphaMeasured: PropTypes.number,
  averageMaeR: PropTypes.number,
  averageMfeR: PropTypes.number,
  payoffRatio: PropTypes.number,
  payoffRatioR: PropTypes.number,
});

/** The session the dialog is handed. */
export const SESSION_SHAPE = PropTypes.shape({
  trades: PropTypes.arrayOf(TRADE_SHAPE).isRequired,
  clicks: PropTypes.number.isRequired,
  selectedId: PropTypes.string,
});

function Row({ label, value, note = null, color = null }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, py: 0.25 }}>
      <Typography sx={{ fontSize: 11, color: glassText(0.55), minWidth: 148, flex: '0 0 auto' }}>
        {label}
      </Typography>
      <Typography sx={{ ...mono, fontSize: 12.5, fontWeight: 700,
        color: color || glassText(0.95) }}>
        {value}
      </Typography>
      {note ? (
        <Typography sx={{ fontSize: 10.5, color: glassText(0.45), minWidth: 0 }}>
          {note}
        </Typography>
      ) : null}
    </Box>
  );
}
Row.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  note: PropTypes.string,
  color: PropTypes.string,
};

function Stat({ label, value, color = null }) {
  return (
    <Box sx={{ minWidth: 92 }}>
      <Typography sx={{ fontSize: 9.5, letterSpacing: 0.6, textTransform: 'uppercase',
        color: glassText(0.5) }}>
        {label}
      </Typography>
      <Typography sx={{ ...mono, fontSize: 15, fontWeight: 800, color: color || glassText(1) }}>
        {value}
      </Typography>
    </Box>
  );
}
Stat.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  color: PropTypes.string,
};


/** The session shape the dialog is handed. */
export const MANUAL_SHAPE = PropTypes.shape({
  phase: PropTypes.oneOf(Object.values(PHASES)).isRequired,
  element: PropTypes.object,
  direction: PropTypes.oneOf(['long', 'short']),
  open: PropTypes.object,
  refusal: PropTypes.string,
});

/**
 * THE EXECUTION STRIP — two permanent action buttons, and the sentence saying what to do next.
 *
 * <h2>⚠ THE MODE TOGGLES ARE GONE</h2>
 *
 * <p>LONG and SHORT used to be a MODE: press one to lock a direction, and only then does the
 * next click mean anything. That was a step whose entire content was "the next click is an
 * open", which is what a button labelled OPEN LONG says by itself. So there are two actions,
 * always available, and no pre-selection of anything.
 *
 * <h2>Once a position exists there is ONE button, not two</h2>
 *
 * <p>Opening the other way is not a thing the machine can do from here — it would re-sign a
 * P&L the reader already watched being built — so offering it greyed out beside the live one
 * would be a control that exists only to be refused. The single CLOSE button also carries the
 * direction, which is the one place on the card that says which way the open position is.
 *
 * <h2>Cancel is always enabled, and that is the requirement rather than an oversight</h2>
 *
 * <p>Flat it has nothing to abort and still answers: pressing it returns the panel to a known
 * state, which is what a reader reaches for when they have lost track of where they are. A
 * control that is sometimes there and sometimes not is one they have to look for first.
 */
function ExecutionStrip({ manual, onOpen, onCancel, replay = false }) {
  const open = manual.phase === PHASES.AWAITING_CLOSE;
  const dir = manual.direction === 'short' ? 'short' : 'long';
  const colourOf = d =>
    (d === 'short' ? OUTCOME_COLORS[OUTCOMES.LOSS] : OUTCOME_COLORS[OUTCOMES.WIN]);

  const action = (d, label, onClick) => {
    const armed = !open && manual.phase === PHASES.AWAITING_OPEN && manual.direction === d;
    const colour = colourOf(d);
    return (
      <Button
        size="small"
        key={d}
        data-testid={`trade-sim-${open ? 'close' : 'open'}-${d}`}
        onClick={onClick}
        sx={{
          flex: 1, fontSize: 12, fontWeight: 800, letterSpacing: 0.6, py: 0.4,
          border: `1px solid ${armed || open ? colour : glassText(0.3)}`,
          // FILLED ONLY WHEN IT IS THE LIVE ONE, so the card always says, at a glance, whether
          // the next bar click opens, closes, or does nothing.
          bgcolor: armed || open ? `${colour}26` : 'transparent',
          color: armed || open ? colour : glassText(0.85),
        }}
      >
        {label}
      </Button>
    );
  };

  return (
    <Box data-testid="trade-sim-strip" sx={{ mt: 1.25 }}>
      <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'stretch' }}>
        {open
          ? action(dir, `CLOSE ${dir.toUpperCase()}`, () => {})
          : [action('long', 'OPEN LONG', () => onOpen('long')),
            action('short', 'OPEN SHORT', () => onOpen('short'))]}
        <Button
          size="small"
          data-testid="trade-sim-cancel"
          onClick={onCancel}
          sx={{ fontSize: 11, color: glassText(0.6), border: `1px solid ${glassText(0.18)}`,
            px: 1.25 }}
        >
          CANCEL
        </Button>
      </Box>
      <Typography data-testid="trade-sim-prompt"
        sx={{ fontSize: 11.5, color: glassText(0.8), mt: 0.75, lineHeight: 1.45 }}>
        {promptFor(manual, { replay })}
      </Typography>
      {manual.refusal ? (
        <Typography data-testid="trade-sim-refusal"
          sx={{ fontSize: 11, color: OUTCOME_COLORS[OUTCOMES.OPEN], mt: 0.5, lineHeight: 1.45 }}>
          {manual.refusal}
        </Typography>
      ) : null}
      {manual.open ? (
        <Typography sx={{ ...mono, fontSize: 11, color: glassText(0.6), mt: 0.5 }}>
          {`OPEN ${manual.direction === 'short' ? 'SHORT' : 'LONG'} `
            + `${manual.open.price.toFixed(2)} @ ${manual.open.date}`
            + (Number.isFinite(manual.open.aimPrice)
              && Math.abs(manual.open.aimPrice - manual.open.price) > 0.005
              ? `   (you clicked ${manual.open.aimPrice.toFixed(2)})` : '')}
        </Typography>
      ) : null}
    </Box>
  );
}
ExecutionStrip.propTypes = {
  replay: PropTypes.bool,
  manual: MANUAL_SHAPE.isRequired,
  onOpen: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

/**
 * The guidance line, which is the whole of what this tool claims to be.
 *
 * <p>Said on every channel card rather than once in a tooltip somebody may never open. The
 * feature tracks and scores what a reader does; it does not tell them to do it.
 */
function GuidanceNote() {
  return (
    <Typography data-testid="trade-sim-guidance"
      sx={{ fontSize: 10, color: glassText(0.42), mt: 0.75, lineHeight: 1.45 }}>
      Educational simulation. The structure above is guidance and the scoring below is a record
      of your own clicks — no trade is taken, and nothing here is advice.
    </Typography>
  );
}

/**
 * THE PROGRESSION GATE'S BANNER — what the chart is doing, and the way out of it.
 *
 * <h2>It is shown because an invisible constraint reads as a broken chart</h2>
 *
 * <p>A reader who clicks thirty bars ahead and moves one has to be told why, on the panel they
 * are looking at and not only in a status line that the next event overwrites. The remaining
 * count is there so the commitment is finite and visible rather than open-ended.
 *
 * <h2>And RELEASE is offered, deliberately</h2>
 *
 * <p>The gate is a discipline, not a lock: its value is in making the jump a deliberate act,
 * which one labelled button preserves and an inescapable constraint does not. Releasing leaves
 * the trade OPEN in the ledger — the honest record of a trade the reader declined to walk out,
 * and one every session rate already excludes.
 */
function GateBanner({ gate, remaining, onRelease }) {
  if (!gate) return null;
  return (
    <Box data-testid="trade-sim-gate" sx={{ display: 'flex', alignItems: 'center', gap: 1,
      mb: 1, px: 1, py: 0.75, borderRadius: 0.5,
      border: `1px solid ${OUTCOME_COLORS[OUTCOMES.OPEN]}`,
      bgcolor: 'rgba(245,158,11,0.10)' }}>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 0.4,
          color: OUTCOME_COLORS[OUTCOMES.OPEN] }}>
          WALKING THE TRADE OUT
        </Typography>
        <Typography sx={{ fontSize: 10, color: glassText(0.6), lineHeight: 1.45 }}>
          {`The ${gate.provider || ''} trade${gate.date ? ` taken on ${gate.date}` : ''} `}
          {'has not resolved, so the replay advances one bar at a time until it hits its '}
          {'target, its stop, or the horizon'}
          {remaining > 0 ? ` — up to ${remaining} more ${remaining === 1 ? 'bar' : 'bars'}.` : '.'}
        </Typography>
      </Box>
      <Button size="small" onClick={onRelease} data-testid="trade-sim-release"
        sx={{ fontSize: 10, color: glassText(0.7), minWidth: 0, px: 0.75, flex: '0 0 auto' }}>
        Release
      </Button>
    </Box>
  );
}
GateBanner.propTypes = {
  gate: PropTypes.shape({
    provider: PropTypes.string,
    date: PropTypes.string,
    entryIndex: PropTypes.number,
    horizonIndex: PropTypes.number,
  }),
  remaining: PropTypes.number,
  onRelease: PropTypes.func.isRequired,
};

/**
 * THE EXECUTION TELEMETRY — what the trade did on its way to the result above it.
 *
 * <h2>Its own heading, because it answers a third question</h2>
 *
 * <p>The card already separates what was KNOWABLE at bar t from what the trade DID. These rows
 * are neither: they are how it got there — how far it went against you before it worked, how
 * much of the favourable move it gave back, and whether it beat simply holding. A desk reads
 * them beside a P&L and not inside one.
 *
 * <h2>MAE and MFE are shown in R first</h2>
 *
 * <p>"0.6R against before it paid 1.0R" is comparable across instruments and across decades;
 * "1.84 points" is not. The percent sits beside it as the scale-free second reading, and both
 * fall back to percent alone for a manual trade, which has no stop and therefore no R.
 *
 * <h2>And ALPHA is labelled for the benchmark it actually used</h2>
 *
 * <p>"vs buy-and-hold", not "vs SPY" — see `tradeSimMetrics`. On a SPY chart the two are the
 * same number; on any other chart the honest label is the one naming what was computed.
 */
function Telemetry({ trade }) {
  const hasExcursion = Number.isFinite(trade.maePercent) || Number.isFinite(trade.mfePercent);
  const hasAlpha = Number.isFinite(trade.alphaPercent);
  if (!hasExcursion && !hasAlpha) return null;
  const excursion = (r, p) => (Number.isFinite(r)
    ? `${r.toFixed(2)}R  (${pct(p)})`
    : (Number.isFinite(p) ? pct(p) : '—'));
  return (
    <Box data-testid="trade-sim-telemetry" sx={{ mt: 1.25 }}>
      <Typography sx={{ fontSize: 9.5, letterSpacing: 0.6, textTransform: 'uppercase',
        color: glassText(0.4) }}>
        Execution telemetry
      </Typography>
      {hasExcursion ? (
        <>
          <Row label="Max adverse (MAE)" value={excursion(trade.maeR, trade.maePercent)}
            note="worst move against the position while it was held" />
          <Row label="Max favourable (MFE)" value={excursion(trade.mfeR, trade.mfePercent)}
            note="best move in its favour, whether or not it was captured" />
        </>
      ) : null}
      {hasAlpha ? (
        <>
          <Row label="Buy-and-hold, same bars" value={pct(trade.holdReturnPercent)} />
          <Row label="Alpha vs buy-and-hold" value={pct(trade.alphaPercent)}
            color={trade.alphaPercent >= 0 ? OUTCOME_COLORS[OUTCOMES.WIN]
              : OUTCOME_COLORS[OUTCOMES.LOSS]}
            note="the excess this trade earned over holding the same instrument" />
        </>
      ) : null}
    </Box>
  );
}
Telemetry.propTypes = { trade: TRADE_SHAPE.isRequired };

/**
 * The CTS card, and now the reader's own half of it.
 *
 * <p>`manual` is passed so a BOTH family can carry its execution strip INSIDE its own card
 * rather than above it: CTS already prints its odds and its assessed result, and a separate
 * channel panel over the top would print the same metadata twice. PTA and PTB are MANUAL-only
 * and have no card of their own, so they get the panel.
 */
function SignalCard({ trade = null }) {
  if (!trade) {
    return (
      <Typography sx={{ fontSize: 12, color: glassText(0.6), py: 2 }}>
        Click a CTS marker on the chart to evaluate it.
      </Typography>
    );
  }
  const adapter = providerFor(trade.provider);
  if (!adapter) {
    // REFUSED, not defaulted. Running CTS's volatility rule under another family's name and
    // reporting it as that family's result is the one outcome this seam exists to prevent.
    return (
      <Typography sx={{ fontSize: 12, color: OUTCOME_COLORS[OUTCOMES.OPEN], py: 2 }}>
        {`No simulation rules are defined for ${trade.provider} signals yet.`}
      </Typography>
    );
  }
  const colour = OUTCOME_COLORS[trade.outcome] || glassText(0.8);
  /*
   * A SIGNAL PAST THE REPLAY BOUNDARY HAS NOTHING TO SHOW, AND SAYS SO ONCE.
   *
   * Without this the card fell through to the ordinary layout and printed the stub: "insufficient
   * prior history · 0 prior resolved setups", "regime: not yet established", and a result heading
   * reading "bars t+1 to t+0". Every one of those is a true statement about the stub and a FALSE
   * explanation of why there is nothing here — the reason is the playhead, not a cold start, and
   * a reader told "insufficient history" on a 2008 point with fifteen years behind it would
   * reasonably conclude the engine was broken.
   */
  const beyond = !!(trade.signal && trade.signal.beyondBoundary);
  // `null` past the boundary, because the caller derives it from price at a bar the replay is
  // hiding — so there is no chip to draw rather than a coin-flip one.
  const long = trade.direction === 'down' ? false : (trade.direction ? true : null);
  // The TRADE says so, not the adapter. A manual trade's shape is what the card has to render,
  // and reading it off the provider would make a CTS trade stored under a channel key render
  // as two legs it does not have.
  const manualTradeShape = !!trade.manual;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
        <Typography sx={{ ...mono, fontSize: 13, fontWeight: 800, color: glassText(1) }}>
          {trade.date}
        </Typography>
        {long === null ? null : (
          <Box sx={{ px: 0.75, py: 0.1, borderRadius: 0.5, fontSize: 10, fontWeight: 800,
            letterSpacing: 0.5,
            bgcolor: long ? 'rgba(16,185,129,0.18)' : 'rgba(239,68,68,0.18)',
            color: long ? OUTCOME_COLORS[OUTCOMES.WIN] : OUTCOME_COLORS[OUTCOMES.LOSS] }}>
            {long ? 'LONG' : 'SHORT'}
          </Box>
        )}
        <Typography sx={{ fontSize: 10.5, color: glassText(0.45) }}>
          {adapter.label}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {trade.outcome ? (
          <Box data-testid="trade-sim-outcome" sx={{ px: 1, py: 0.2, borderRadius: 0.5,
            fontSize: 11.5, fontWeight: 800, letterSpacing: 0.6,
            border: `1px solid ${colour}`, color: colour }}>
            {OUTCOME_LABELS[trade.outcome]}
          </Box>
        ) : null}
      </Box>

      {beyond ? (
        /* ONE block, one statement. Not a Signal section with stub rows and a Result section
           underneath saying the same thing twice in two different wordings. */
        <Box data-testid="trade-sim-beyond" sx={{ mt: 1 }}>
          <Typography sx={{ fontSize: 9.5, letterSpacing: 0.6, textTransform: 'uppercase',
            color: glassText(0.4) }}>
            Not assessed
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: OUTCOME_COLORS[OUTCOMES.OPEN], py: 0.5,
            lineHeight: 1.5 }}>
            {trade.exitReason}
          </Typography>
          <Typography sx={{ fontSize: 10, color: glassText(0.4), lineHeight: 1.45 }}>
            This point sits past the replay playhead, so its own bar is not drawn. Nothing about
            it is knowable yet — not its direction, not its context, and not what it went on to
            do. Step the replay forward and click it again.
          </Typography>
        </Box>
      ) : (
      <>
      {/* ── the context the decision was made in ────────────────────────────────────────
          Worded by MODE. For CTS the heading is a no-look-ahead claim and has to be exact; for
          a channel the geometry is simply what the element is, and borrowing the CTS wording
          would assert a left-only guarantee about rows that are not an estimate at all. */}
      <Typography sx={{ fontSize: 9.5, letterSpacing: 0.6, textTransform: 'uppercase',
        color: glassText(0.4), mt: 1 }}>
        {manualTradeShape ? 'Structure' : 'Signal — measured from bars 0 to t−1'}
      </Typography>
      {adapter.metadata(trade.signal).map(r => (
        <Row key={r.key} label={r.label} value={r.value} note={r.note} />
      ))}

      {/* ── and what the trade then did ─────────────────────────────────────────────────── */}
      <Typography sx={{ fontSize: 9.5, letterSpacing: 0.6, textTransform: 'uppercase',
        color: glassText(0.4), mt: 1.25 }}>
        {manualTradeShape
          ? 'Result — your own open and close'
          : `Result — measured over bars t+1 to t+${trade.horizon}`}
      </Typography>
      {!trade.priced ? (
        <Typography sx={{ fontSize: 11.5, color: OUTCOME_COLORS[OUTCOMES.OPEN], py: 0.5 }}>
          {trade.exitReason}
        </Typography>
      ) : (
        <>
          {manualTradeShape ? (
            <>
              <Row label="Open" value={price(trade.openLeg.price)}
                note={`${trade.openLeg.date} · bar ${trade.openLeg.index}`} />
              <Row label="Close" value={price(trade.closeLeg.price)}
                note={`${trade.closeLeg.date} · bar ${trade.closeLeg.index}`} />
              <Row label="Held" value={`${trade.barsHeld} bar${trade.barsHeld === 1 ? '' : 's'}`} />
              <Row label="Net move" value={`${signed(trade.pnl)}  (${pct(trade.pnlPercent)})`}
                color={colour} />
            </>
          ) : (
            <>
          <Row label="Entry (trigger close)" value={price(trade.entryPrice)} />
          <Row label="Target" value={price(trade.target)}
            note={`${trade.targetAtr.toFixed(1)} ATR`} />
          <Row label="Stop" value={price(trade.stop)}
            note={`${trade.stopAtr.toFixed(1)} ATR`} />
          {trade.outcome === OUTCOMES.OPEN ? (
            <>
              <Row label="Marked at" value={price(trade.markPrice)}
                note={`${trade.barsHeld} bars so far — the horizon is past the last bar`} />
              <Row label="Unrealised" value={pct(trade.markPnlPercent)}
                color={OUTCOME_COLORS[OUTCOMES.OPEN]}
                note="a mark, not a result — excluded from the session rates below" />
            </>
          ) : (
            <>
              <Row label="Exit" value={price(trade.exitPrice)}
                note={`${trade.exitReason} · ${trade.barsHeld} bar${trade.barsHeld === 1 ? '' : 's'}`} />
              <Row label="Net move" value={`${signed(trade.pnl)}  (${pct(trade.pnlPercent)})`}
                color={colour} />
              <Row label="R multiple" value={signed(trade.rMultiple)} color={colour}
                note="net move over the entry-to-stop distance" />
            </>
          )}
            </>
          )}
          <Telemetry trade={trade} />
        </>
      )}
      {/* The rule summary describes an assessment that did not happen, so it goes with it. */}
      <Typography sx={{ fontSize: 10, color: glassText(0.4), mt: 0.75, lineHeight: 1.45 }}>
        {adapter.ruleSummary()}
      </Typography>
      </>
      )}

    </Box>
  );
}
SignalCard.propTypes = { trade: TRADE_SHAPE };


/**
 * THE SELECTED STRUCTURE — geometry, and nothing to press.
 *
 * <p>It used to carry the execution strip too, because the strip only existed once something
 * was selected. Now the strip is permanent and this is pure CONTEXT: the channel a reader
 * clicked a handle on, kept on screen so they can read its levels while they trade. A trade
 * opened while it is showing carries its name into the ledger; nothing has to be selected to
 * trade at all.
 */
function ChannelContext({ element }) {
  const adapter = providerFor(element.provider);
  if (!adapter) return null;
  return (
    <Box data-testid="trade-sim-channel" sx={{ pb: 1.25, mb: 1.25,
      borderBottom: `1px solid ${glassText(0.12)}` }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Typography sx={{ ...mono, fontSize: 13, fontWeight: 800, color: glassText(1) }}>
          {element.label}
        </Typography>
        <Typography sx={{ fontSize: 10.5, color: glassText(0.45) }}>{adapter.label}</Typography>
      </Box>
      {adapter.metadata(element).map(r => (
        <Row key={r.key} label={r.label} value={r.value} note={r.note} />
      ))}
      <Typography sx={{ fontSize: 10, color: glassText(0.4), mt: 0.75, lineHeight: 1.45 }}>
        {adapter.ruleSummary()}
      </Typography>
    </Box>
  );
}
ChannelContext.propTypes = { element: PropTypes.object.isRequired };

function SessionLedger({ summary, onReset }) {
  const pnlColour = summary.realised === 0 ? glassText(0.8)
    : summary.totalNetPnlPercent >= 0 ? OUTCOME_COLORS[OUTCOMES.WIN] : OUTCOME_COLORS[OUTCOMES.LOSS];
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
        <Typography sx={{ fontSize: 9.5, letterSpacing: 0.6, textTransform: 'uppercase',
          color: glassText(0.4) }}>
          Session ledger
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button size="small" onClick={onReset} data-testid="trade-sim-reset"
          sx={{ fontSize: 10, color: glassText(0.6), minWidth: 0, px: 0.75 }}>
          Reset
        </Button>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, rowGap: 1 }}>
        <Stat label="Clicked" value={summary.clicks} />
        {/* SIGNALS, not "Evaluated": a point past the replay boundary is in this ledger and
            was deliberately NOT evaluated, so the old label contradicted its own row. The tile
            counts DISTINCT signals, which is why it can differ from Clicked above it. */}
        <Stat label="Signals" value={summary.evaluated} />
        <Stat label="Win rate"
          value={summary.realised === 0 ? 'n/a' : `${summary.winRatePercent.toFixed(1)}%`} />
        <Stat label="Profit factor" value={ratio(summary.profitFactor)} />
        <Stat label="Net P&L" value={summary.realised === 0 ? 'n/a'
          : pct(summary.totalNetPnlPercent)} color={pnlColour} />
        <Stat label="Net points" value={summary.realised === 0 ? 'n/a'
          : signed(summary.totalPoints)} color={pnlColour} />
        {/* WITHHELD unless every realised trade has an R. A manual trade has no stop, and a
            mean dragged toward zero by trades that never had one is worse than no figure. */}
        <Stat label="Mean R" value={summary.realised > 0 && summary.rMeasured === summary.realised
          ? signed(summary.averageRMultiple) : 'n/a'} />
      </Box>
      {/* ── the institutional half ────────────────────────────────────────────────────────
          A second row rather than more tiles in the first: these are the figures a portfolio
          manager reads AFTER the headline, and mixing them into it would bury the win rate.
          Every one is `n/a` rather than 0 when its inputs are absent — a session of manual
          trades has no R at all, and a mean of nothing is not zero. */}
      <Box data-testid="trade-sim-institutional"
        sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, rowGap: 1, mt: 1, pt: 1,
          borderTop: `1px solid ${glassText(0.1)}` }}>
        <Stat label="Alpha" value={summary.alphaMeasured === 0 ? 'n/a'
          : pct(summary.alphaPercent)}
        color={!Number.isFinite(summary.alphaPercent) ? null
          : summary.alphaPercent >= 0 ? OUTCOME_COLORS[OUTCOMES.WIN]
            : OUTCOME_COLORS[OUTCOMES.LOSS]} />
        <Stat label="Buy & hold" value={summary.alphaMeasured === 0 ? 'n/a'
          : pct(summary.holdReturnPercent)} />
        <Stat label="Payoff (R)" value={ratio(summary.payoffRatioR)} />
        <Stat label="Payoff (%)" value={ratio(summary.payoffRatio)} />
        <Stat label="Avg MAE" value={Number.isFinite(summary.averageMaeR)
          ? `${summary.averageMaeR.toFixed(2)}R` : 'n/a'} />
        <Stat label="Avg MFE" value={Number.isFinite(summary.averageMfeR)
          ? `${summary.averageMfeR.toFixed(2)}R` : 'n/a'} />
      </Box>
      <Typography sx={{ fontSize: 10, color: glassText(0.4), mt: 0.5, lineHeight: 1.45 }}>
        {/* The benchmark is NAMED, because a figure that claims a benchmark it did not use is
            worse than one that names the benchmark it did. */}
        Alpha is the compounded excess over buy-and-hold of this instrument across exactly the
        bars each trade was held. Payoff (R) divides the average win by the average loss in R
        rather than in percent, so each trade is normalised by the volatility it was taken in.
      </Typography>
      <Typography sx={{ ...mono, fontSize: 10.5, color: glassText(0.5), mt: 1 }}>
        {`${summary.wins}W / ${summary.losses}L / ${summary.neutral} neutral`}
        {summary.open > 0 ? ` · ${summary.open} open` : ''}
        {summary.unpriced > 0 ? ` · ${summary.unpriced} unpriced` : ''}
      </Typography>
      {summary.open > 0 || summary.unpriced > 0 ? (
        <Typography sx={{ fontSize: 10, color: glassText(0.4), mt: 0.5, lineHeight: 1.45 }}>
          {/* NAMED rather than silently dropped. A rate computed over fewer trades than the
              reader clicked, with nothing saying so, is the quiet kind of wrong. */}
          Open and unpriced signals are excluded from every rate above — an unresolved trade has
          no outcome, and counting its mark would make a recent click look flat for the sole
          reason that time has not passed.
        </Typography>
      ) : null}
    </Box>
  );
}
SessionLedger.propTypes = {
  summary: SUMMARY_SHAPE.isRequired,
  onReset: PropTypes.func.isRequired,
};

export default function TradeSimulatorDialog({
  // DEFAULT PARAMETERS, not `defaultProps`: React warns that the static form is going away for
  // function components, and it warned on every render of every test in this suite.
  open, session, onClose, onReset,
  manual = null, onOpenTrade = () => {}, onCancelManual = () => {},
  gate = null, gateRemaining = 0, onReleaseGate = () => {}, replay = false,
}) {
  // react-draggable writes its own transform onto the node, so the panel cannot be centred with
  // one as well — a flex wrapper does it instead and the drag stays a clean offset FROM centre.
  const nodeRef = useRef(null);
  if (!open) return null;

  const trade = selectedTrade(session);
  const summary = sessionSummary(session);
  /*
    * Whatever STRUCTURE the card is showing, if any. CONTEXT: a reader can trade with nothing
    * selected, and a trade taken while this is on screen carries its name into the ledger.
    *
    * A family that ASSESSES is excluded, because its own card is already below — a CTS
    * element here would print its odds and its regime twice, a centimetre apart, which is
    * exactly what the separate channel panel was removed for.
    */
  const context = manual && manual.element
    && !assessesAutomatically(manual.element.provider)
    ? manual.element : null;

  return (
    <Box
      data-testid="trade-sim-centering"
      sx={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        /*
         * TOP-LEFT, not centred.
         *
         * This panel is driven by clicking the chart behind it, and a centred panel sits over
         * the price action a reader is aiming at — on a wide chart the middle is exactly where
         * the recent bars and the channel handles are. The top-left quadrant is the emptiest
         * part of a price chart that rises to the right, and it is also where the drag starts
         * from, so the offset the reader drags is measured from a corner rather than from a
         * centre that moves with the window.
         *
         * Flex rather than `top`/`left` on the panel itself: react-draggable writes its own
         * `transform`, and the two cannot share that property — the first drag would discard a
         * translate-based position and jump.
         */
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        p: 1.5,
        // Or the full-viewport wrapper swallows every click on the chart, which would stop the
        // reader clicking the next CTS point — the one gesture this dialog is driven by.
        pointerEvents: 'none',
        zIndex: 1350,
      }}
    >
      <Draggable handle=".trade-sim-drag" nodeRef={nodeRef}>
        <Box
          ref={nodeRef}
          data-testid="trade-sim-dialog"
          sx={{
            ...glassSurface(),
            position: 'relative',
            pointerEvents: 'auto',
            width: 430,
            maxWidth: 'calc(100vw - 48px)',
            // Measured from the TOP now, not from a centred panel's two margins, so the height
            // budget is the window less this wrapper's padding and the app bar above it.
            maxHeight: 'calc(100vh - 160px)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box className="trade-sim-drag" sx={{
            display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 1,
            borderBottom: `1px solid ${glassText(0.12)}`, cursor: 'move', flex: '0 0 auto',
          }}>
            <DragIndicatorIcon sx={{ fontSize: 18, color: glassText(0.35) }} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 800, letterSpacing: 0.4,
                color: glassText(1), lineHeight: 1.2 }}>
                Interactive Trade Simulator
              </Typography>
              <Typography noWrap sx={{ ...mono, fontSize: 10.5, color: glassText(0.6) }}>
                {/* The ARMED element wins the subtitle. It is what the next click acts on, and
                    a header naming the last booked trade while the chart is waiting for an
                    Open would describe the thing that is NOT about to happen. */}
                {manual && manual.element
                  ? `${manual.element.provider} · ${manual.element.label}`
                  : trade ? `${trade.provider} · ${trade.date}` : 'awaiting a signal'}
              </Typography>
            </Box>
            <Tooltip title="Close — the session ledger is kept">
              <IconButton size="small" onClick={onClose} data-testid="trade-sim-close"
                sx={{ color: glassText(0.6) }}>
                <CloseIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          </Box>

          <Box sx={{ px: 1.5, py: 1.25, overflowY: 'auto', flex: '1 1 auto' }}>
            {/* FIRST in the scroll area, above whatever card is showing. The gate constrains
                the CHART rather than one trade, so it belongs at the top of the panel and not
                buried inside the card of the trade that happened to arm it. */}
            <GateBanner gate={gate} remaining={gateRemaining} onRelease={onReleaseGate} />
            {context ? <ChannelContext element={context} /> : null}
            <SignalCard trade={trade} />

            {/* ── YOUR EXECUTION — permanent, and the one place a trade is taken ────────────
                Not inside the card and not inside the channel panel, which is where it lived
                when it needed one of them to have been selected first. Two always-available
                actions cannot sit inside a block that is conditionally rendered, and the
                brief's requirement that the assessment above and the execution status are
                clearly separated is better served by its own heading than by a divider. */}
            {manual ? (
              <Box data-testid="trade-sim-own-execution" sx={{ mt: 1.5, pt: 1.25,
                borderTop: `1px solid ${glassText(0.12)}` }}>
                <Typography sx={{ fontSize: 9.5, letterSpacing: 0.6,
                  textTransform: 'uppercase', color: glassText(0.4) }}>
                  Your execution
                </Typography>
                <ExecutionStrip manual={manual} onOpen={onOpenTrade}
                  onCancel={onCancelManual} replay={replay} />
                <GuidanceNote />
              </Box>
            ) : null}
          </Box>

          <Box sx={{ px: 1.5, py: 1.25, flex: '0 0 auto',
            borderTop: `1px solid ${glassText(0.12)}` }}>
            <SessionLedger summary={summary} onReset={onReset} />
          </Box>
        </Box>
      </Draggable>
    </Box>
  );
}

TradeSimulatorDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  session: SESSION_SHAPE.isRequired,
  /** The manual execution machine. Absent means no channel is armed, which is the CTS case. */
  manual: MANUAL_SHAPE,
  onClose: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  /** Called with 'long' or 'short' when the reader presses an OPEN button. */
  onOpenTrade: PropTypes.func,
  onCancelManual: PropTypes.func,
  gate: PropTypes.shape({
    provider: PropTypes.string,
    date: PropTypes.string,
    entryIndex: PropTypes.number,
    horizonIndex: PropTypes.number,
  }),
  gateRemaining: PropTypes.number,
  onReleaseGate: PropTypes.func,
  /** True while a replay is running, which is the only time the grid is split into zones. */
  replay: PropTypes.bool,
};

